import { flexRender } from '@tanstack/react-table';
import React__default, { useCallback, useRef, useState, useLayoutEffect } from 'react';
import { TBody, TRow, LoadingState, TCell, getCellStyles, ExpandableContent, ExpandableInner } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { density } from '@hubble/tokens/web';

function AnimatedExpandableContent({
  isExpanded,
  children,
  expandAnimationDuration
}) {
  return jsx(ExpandableContent, {
    isExpanded: isExpanded,
    expandAnimationDuration: expandAnimationDuration,
    children: jsx(ExpandableInner, {
      children: children
    })
  });
}
function TableBodyRegular({
  rowSize,
  userColDefsById,
  table,
  loading,
  visibleColumnCount,
  scrollRef,
  density,
  resizable,
  handleRowClick,
  enableExpanding,
  renderSubComponent,
  getRowProps,
  expandAnimationDuration
}) {
  return jsx(TBody, {
    ref: scrollRef,
    children: table.getRowModel().rows.map(row => jsxs(React__default.Fragment, {
      children: [jsx(TableRow, {
        row: row,
        rowSize: rowSize,
        loading: loading,
        userColDefsById: userColDefsById,
        visibleColumnCount: visibleColumnCount,
        density: density,
        windowed: false,
        onClick: handleRowClick,
        resizable: resizable,
        enableExpanding: enableExpanding,
        getRowProps: getRowProps,
        isExpanded: enableExpanding && row.getIsExpanded(),
        expandAnimationDuration: expandAnimationDuration
      }), enableExpanding && renderSubComponent && jsx(AnimatedExpandableContent, {
        isExpanded: row.getIsExpanded(),
        expandAnimationDuration: expandAnimationDuration,
        children: renderSubComponent({
          row: row.original
        })
      })]
    }, row.id))
  });
}
const MemoizedTableBodyRegular = React__default.memo(TableBodyRegular, (prev, next) => prev.table.options.data === next.table.options.data);
function TableRow({
  rowSize,
  userColDefsById,
  row,
  loading,
  density,
  windowed,
  resizable,
  onClick,
  enableExpanding,
  getRowProps,
  isExpanded,
  expandAnimationDuration
}) {
  const additionalProps = getRowProps ? getRowProps(row.original) : {};
  const {
    onClick: customOnClick,
    ...otherAdditionalProps
  } = additionalProps;
  const mergedOnClick = useCallback(e => {
    if (loading || row.original.loading) return;
    if (onClick) {
      onClick(e);
    }
    if (customOnClick) {
      customOnClick(e);
    }
  }, [onClick, customOnClick, loading, row.original.loading]);
  return jsx(TRow, {
    density: density,
    "aria-hidden": loading,
    windowed: windowed,
    isExpanded: isExpanded,
    expandAnimationDuration: expandAnimationDuration,
    onClick: mergedOnClick,
    "data-id": row.id,
    style: {
      cursor: enableExpanding && row.getCanExpand() ? 'pointer' : undefined,
      ...otherAdditionalProps.style
    },
    ...otherAdditionalProps,
    children: row.getVisibleCells().map(({
      column,
      id,
      getContext
    }) => {
      const isLoading = loading || row.original.loading;
      const cellContent = isLoading ? jsx(LoadingState, {
        size: rowSize,
        variant: column.columnDef.loader,
        justifyContent: column.columnDef.align
      }) : flexRender(column.columnDef.cell, getContext());
      const rawValue = isLoading ? '' : row.getValue(column.id);
      const cellTitle = rawValue ? String(rawValue) : '';
      return jsx(TCell, {
        justifyContent: column.columnDef.align,
        size: rowSize,
        style: getCellStyles(column, userColDefsById[column.id]),
        "data-column-id": column.id,
        "data-testid": `table-cell-${id}`,
        density: density,
        resizable: resizable,
        title: cellTitle,
        ...(resizable ? {
          style: {
            width: `calc(var(--col-${id.substring(id.indexOf('_') + 1)}-size) * 1px)`
          }
        } : {}),
        children: cellContent
      }, id);
    })
  });
}
const onScroll = (e, rowHeight, from, setFrom, rowBuffer) => {
  const newPos = Math.trunc(e.target.scrollTop / rowHeight);
  const delta = Math.abs(from - newPos);
  if (delta >= rowBuffer) {
    if (e.target.renderTaskId) {
      cancelAnimationFrame(e.target.renderTaskId);
    }
    e.target.renderTaskId = requestAnimationFrame(() => setFrom(newPos));
  }
};
const rowBuffer = 12;
function TableBodyWindowed({
  rowSize,
  userColDefsById,
  table,
  loading,
  visibleColumnCount,
  scrollRef,
  density: density$1,
  resizable,
  handleRowClick,
  enableExpanding,
  renderSubComponent,
  getRowProps,
  expandAnimationDuration
}) {
  const ref = useRef();
  const [height, setHeight] = useState(0);
  const rows = table.getRowModel().rows;
  const rowHeight = density$1 ? Number(density.contentItems.height[density$1].replace('px', '')) : rowSize === 'md' ? 52 : 84;
  const [from, setFrom] = useState(0);
  const start = Math.max(from - rowBuffer, 0);
  const end = Math.min(from + Math.trunc(height / rowHeight) + rowBuffer, rows.length);
  const tHeight = rowHeight * start;
  const bHeight = rowHeight * (rows.length - end);
  const visibleRows = rows.slice(start, end);
  const columnCount = Object.keys(userColDefsById).length;
  useLayoutEffect(() => {
    const heightObserver = new ResizeObserver(entries => {
      var _entries$;
      const tableEl = (_entries$ = entries[0]) === null || _entries$ === void 0 ? void 0 : _entries$.target;
      if (tableEl) {
        var _tableEl$previousSibl;
        const height = tableEl.parentElement.clientHeight - ((_tableEl$previousSibl = tableEl.previousSibling) === null || _tableEl$previousSibl === void 0 ? void 0 : _tableEl$previousSibl.clientHeight);
        setHeight(height);
      }
    });
    heightObserver.observe(ref.current);
    return () => {
      if (ref.current) {
        heightObserver.unobserve(ref.current);
      } else {
        heightObserver.disconnect();
      }
    };
  }, []);
  const refSet = useCallback(el => {
    if (scrollRef) {
      scrollRef.current = el;
    }
    ref.current = el;
  }, [scrollRef]);
  return jsxs(TBody, {
    ref: refSet,
    onScroll: e => onScroll(e, rowHeight, from, setFrom, rowBuffer),
    overflowY: "auto",
    children: [tHeight > 0 && jsx("tr", {
      children: jsx("td", {
        colSpan: columnCount,
        style: {
          height: tHeight
        }
      })
    }), visibleRows.map(row => jsxs(React__default.Fragment, {
      children: [jsx(TableRow, {
        row: row,
        rowSize: rowSize,
        loading: loading,
        userColDefsById: userColDefsById,
        visibleColumnCount: visibleColumnCount,
        density: density$1,
        windowed: true,
        onClick: handleRowClick,
        resizable: resizable,
        enableExpanding: enableExpanding,
        getRowProps: getRowProps
      }), enableExpanding && renderSubComponent && jsx(AnimatedExpandableContent, {
        isExpanded: row.getIsExpanded(),
        expandAnimationDuration: expandAnimationDuration,
        children: renderSubComponent({
          row: row.original
        })
      })]
    }, row.id)), bHeight > 0 && jsx("tr", {
      children: jsx("td", {
        colSpan: columnCount,
        style: {
          height: bHeight
        }
      })
    })]
  });
}
const MemoizedTableBodyWindowed = React__default.memo(TableBodyWindowed, (prev, next) => prev.table.options.data === next.table.options.data);

export { MemoizedTableBodyRegular, MemoizedTableBodyWindowed, TableBodyRegular, TableBodyWindowed };
